/**
 * Contractor profile — Details tab.
 *
 * The contractor master record (dswms_cntrctr_lst_t via GET ds-wms/contractors/:id):
 * firm, registration, statutory numbers, bank account and capacity. Read-only; the
 * header's Edit button opens ContractorForm for changes.
 */
import type { ReactNode } from 'react';
import { fmtMoney } from '../../lib/money';

const fmtDate = (v: unknown) => {
  if (!v) return '-';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN');
};
const text = (v: unknown) => (v === null || v === undefined || v === '' ? '-' : String(v));

// Show only the last four digits of the account number.
const maskAccount = (v: unknown) => {
  const s = String(v || '');
  return s.length > 4 ? `${'•'.repeat(s.length - 4)}${s.slice(-4)}` : text(s);
};

interface Props {
  record: Record<string, any> | null;
  loading: boolean;
}

const Section = ({ title, icon, rows }: { title: string; icon: string; rows: [string, ReactNode][] }) => (
  <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-200">
      <i className={`${icon} text-sm text-blue-600`}></i>{title}
    </div>
    <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 px-3 py-2.5 sm:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{k}</dt>
          <dd className="truncate text-xs text-gray-800 dark:text-gray-200">{v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

export default function ContractorDetailsTab({ record, loading }: Props) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-36 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />)}
      </div>
    );
  }
  if (!record) {
    return <div className="p-8 text-center text-sm text-gray-500">Contractor details could not be loaded.</div>;
  }
  const r = record;
  const status = r.blklst_in ? 'Blacklisted' : text(r.cntrctr_sts_nm);
  const statusTone = r.blklst_in || /black|suspend|dereg/i.test(String(r.cntrctr_sts_nm))
    ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700';

  return (
    <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
      <Section title="Contractor" icon="ri-building-2-line" rows={[
        ['Name', text(r.cntrctr_nm)],
        ['Code', <span className="font-mono">{text(r.cntrctr_cd)}</span>],
        ['Firm', text(r.frm_nm)],
        ['Category', text(r.cntrctr_ctgry_nm)],
        ['Class', text(r.pwdclass_cd)],
        ['Status', <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${statusTone}`}>{status}</span>],
        ['Established', fmtDate(r.estblshmnt_dt)],
        ['MSME', r.msme_rgstrd_in ? `Yes${r.udyam_rgstrn_nu ? ` · ${r.udyam_rgstrn_nu}` : ''}` : 'No'],
      ]} />
      <Section title="Contact" icon="ri-contacts-line" rows={[
        ['Contact Person', `${text(r.cntct_prsn_nm)}${r.cntct_dsgntn_nm ? ` (${r.cntct_dsgntn_nm})` : ''}`],
        ['Phone', text(r.phn_nu)],
        ['Email', text(r.eml_tx)],
        ['Address', [r.addr_tx, r.cty_nm, r.stte_nm, r.pin_cd].filter(Boolean).join(', ') || '-'],
      ]} />
      <Section title="Registration & Tax" icon="ri-file-shield-2-line" rows={[
        ['Registration No.', text(r.rgstrn_nu)],
        ['Registered On', fmtDate(r.rgstrn_dt)],
        ['Valid Until', fmtDate(r.rgstrn_vld_dt)],
        ['GSTIN', <span className="font-mono">{text(r.gstn_nu)}</span>],
        ['PAN', <span className="font-mono">{text(r.pan_nu)}</span>],
      ]} />
      <Section title="Bank & Capacity" icon="ri-bank-line" rows={[
        ['Account Holder', text(r.bnk_acnt_hldr_nm)],
        ['Account No.', <span className="font-mono">{maskAccount(r.bnk_acnt_nu)}</span>],
        ['IFSC', <span className="font-mono">{text(r.ifsc_cd)}</span>],
        ['Bank / Branch', [r.bnk_nm, r.bnk_brnch_nm].filter(Boolean).join(' · ') || '-'],
        ['Max Tender Capacity', r.max_tndr_cpcty_at ? fmtMoney(r.max_tndr_cpcty_at) : '-'],
        ['Avg. Turnover', r.avg_trnvr_at ? fmtMoney(r.avg_trnvr_at) : '-'],
      ]} />
    </div>
  );
}
